/* eslint-disable react/prop-types */
import React, { Component, useEffect, useRef } from 'react';
import { NumericFormat, PatternFormat } from 'react-number-format';
import * as PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import { connect, getIn, useFormikContext } from 'formik';
import { isEqual, isNil, noop } from 'lodash';

import { withStyles } from '~/assets/withStyles';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import InputAdornment from '~/components/core/Atomic/InputAdornment';
import ListItemText from '~/components/core/Atomic/ListItemText';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Typography from '~/components/core/Atomic/Typography';
import MonetaryValueTextFieldFormik from '~/components/core/Formik/MonetaryValueTextFieldFormik';
import DatePickerField from '~/components/core/Molecules/Fields/DatePickerField';
import MultiSelectField from '~/components/core/Molecules/Fields/MultiSelectField';
import onUserValueChange from '~/components/core/Molecules/Fields/onUserValueChange';
import TextField from '~/components/core/Molecules/Fields/TextField';
import { useRestrictedPermissions } from '~/components/core/Permissions/RestrictedPermissions';
import Checkbox from '~/components/ui/atoms/Checkbox';
import { isoDateToUs, localTimeToViewTime } from '~/DateTimeUtils';
import { hasOptionValue, useAutoSelectSingleOption } from '~/hooks/useAutoSelectSingleOption';
import cn from '~/Utils/cn';

import { shouldShowFieldError, useFormikFieldError } from './core/Formik/formikErrorUtils';
import { useCurrencyFormatter } from './CurrencyFormatterContext';
import { PencilIcon } from './icons';
import InlineIconButton from './InlineIconButton';

import styles, { useStyles } from '../assets/styles';

const ShowOnlyTextField = ({
  classes,
  wrapperClassName = '',
  label,
  disabled,
  onEdit,
  showOnlyValueComponent,
  maxHeight,
  width,
  doNotRenderInTypography,
  staticEditIcon,
  editableValueStyles,
  editTriggerAria,
}) => {
  const { userHasContextPermissions, permissionDeniedTooltipText } = useRestrictedPermissions();

  let valueComponent;
  if (doNotRenderInTypography) {
    valueComponent = showOnlyValueComponent;
  } else {
    valueComponent = (
      <Typography display="block" variant="body1">
        {showOnlyValueComponent}
      </Typography>
    ); // for CSS consistency we wrap in Typography
  }

  const isEditable = onEdit && onEdit !== noop && !disabled;
  valueComponent = (
    // Block-level `flex` (not `inline-flex`): this fills the parent instead of shrink-wrapping to
    // its content, giving the value a definite width to clip/scroll against. With `inline-flex` a
    // long unbroken value may grow the box past the parent and overlay adjacent elements.
    <div style={{ display: 'flex', alignItems: 'center', width }}>
      <span
        style={
          editableValueStyles || {
            maxHeight,
            width,
            overflow: 'auto',
            height: '100%',
            whiteSpace: 'pre-wrap',
          }
        }
      >
        {valueComponent}
      </span>
      {isEditable && (
        <span
          className={
            staticEditIcon ? '' : 'opacity-0 focus-within:opacity-100 group-hover/show-only-text-field:opacity-100'
          }
        >
          <InlineIconButton
            size="small"
            className={cn(classes.inlineEditIcon, classes.hoverableNonFilledIcon)}
            icon={PencilIcon}
            onClick={(e) => onEdit(e)}
            ariaLabel={typeof label === 'string' && label ? `Edit ${label}` : 'Edit'}
            tooltipTitle={userHasContextPermissions ? null : permissionDeniedTooltipText}
            disabled={!userHasContextPermissions}
            {...editTriggerAria}
          />
        </span>
      )}
    </div>
  );

  return (
    // min-w-0 so the field can shrink when it's a flex item (e.g. inside a flex container): without
    // it the default `min-width: auto` keeps the field at its content width, pushing a long value
    // past the column and overlaying neighbors. It's a no-op outside flex contexts, so it's a safe
    // shared default that keeps overflow-tooltip/truncated values contained wherever they're used.
    <div className={cn('group/show-only-text-field min-w-0', classes.showField, wrapperClassName)}>
      <Typography display="block" variant="caption">
        {label}
      </Typography>
      {valueComponent}
    </div>
  );
};

ShowOnlyTextField.propTypes = {
  classes: PropTypes.object.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  showOnlyValueComponent: PropTypes.node,
  disabled: PropTypes.bool,
  onEdit: PropTypes.func,
  maxHeight: PropTypes.string,
  width: PropTypes.string,
  doNotRenderInTypography: PropTypes.bool,
  staticEditIcon: PropTypes.bool,
  wrapperClassName: PropTypes.string,
  editableValueStyles: PropTypes.object,
  // For an edit that opens a popover: the attributes `getPopoverTriggerAria` returns, for the button.
  editTriggerAria: PropTypes.shape({
    'aria-haspopup': PropTypes.oneOf(['dialog']),
    'aria-expanded': PropTypes.bool,
    'aria-controls': PropTypes.string,
  }),
};

const TextFieldFormik = ({
  id,
  disabled = false,
  children = null,
  showOnly = false,
  onEdit = noop,
  multiline = false,
  rows = -1,
  minRows = undefined,
  fullWidth = false,
  clearable = false,
  onClear = noop,
  helperText = '',
  onChange = undefined,
  staticEditIcon = undefined,
  inputMode = undefined,
  type = undefined,
  onKeyDown = undefined,
  onMouseUp = undefined,
  inputWrapperRef = undefined,
  autoSelectSingleOption = true,
  ...rest
}) => {
  const { classes } = useStyles();

  const { setFieldTouched, values, errors, handleBlur, setFieldValue, handleChange } = useFormikContext();
  const { showError, error: fieldError } = useFormikFieldError(id);
  const actualRows = rows === -1 ? undefined : rows;

  // MenuItem children are the options here, so derive the selectable (non-empty, non-disabled) values.
  const isSelect = !!rest.select;
  const isMultipleSelect = isSelect && !!rest.SelectProps?.multiple;
  const optionElements = isSelect ? React.Children.toArray(children).filter(React.isValidElement) : [];
  const selectableOptionValues = optionElements
    .filter((child) => hasOptionValue(child.props.value) && !child.props.disabled)
    .map((child) => child.props.value);

  const currentFieldValue = getIn(values, id);

  // MUI's Select matches an object option value to the field value by identity, so a value that only
  // deep-equals its option - a copy of the array the option holds - renders blank. Find the option by
  // equality and hand MUI its value. Primitives already match by identity, and skip the lookup.
  const isObjectValue = typeof currentFieldValue === 'object' && currentFieldValue !== null;
  const equalOption =
    isSelect && !isMultipleSelect && isObjectValue
      ? optionElements.find((child) => isEqual(child.props.value, currentFieldValue))
      : undefined;
  const inputValue = equalOption ? equalOption.props.value : currentFieldValue;
  useAutoSelectSingleOption({
    enabled: isSelect && autoSelectSingleOption && !disabled && !showOnly && !!id,
    isEmpty: Array.isArray(currentFieldValue) ? !currentFieldValue.length : !hasOptionValue(currentFieldValue),
    options: selectableOptionValues,
    onSelect: (option) => {
      const nextValue = isMultipleSelect ? [option] : option;
      return onChange
        ? onChange({ target: { name: id, value: nextValue } })
        : handleChange({ target: { name: id, value: nextValue } });
    },
  });

  if (showOnly) {
    const { label, value, select, SelectProps } = rest;

    // verify that we have any value to show (and avoid cryptic errors on console)
    if (value === undefined && id === undefined) {
      throw new Error(`TextFieldFormik was requested to render "${label}" but no id or value were give.`);
    }

    // if someone supplied a value, use it, otherwise, lookup the values array
    let currValue = value !== undefined ? value : values && getIn(values, id) !== undefined ? getIn(values, id) : '';

    if (select) {
      // optionElements is the same children already filtered to elements, so conditional rendering's
      // null/false entries are gone before .props is read. Matching by find mirrors the lookup above.
      const selectedOption = optionElements.find((menuItem) => isEqual(menuItem.props.value, currValue));
      if (selectedOption) {
        currValue = selectedOption.props.children;
      }
    }

    if (SelectProps && SelectProps.multiple && SelectProps.renderValue) {
      currValue = SelectProps.renderValue(currValue);
    }

    // A growing field states its floor as `minRows` rather than `rows`, so read whichever is set —
    // otherwise the read-only view of those fields loses its height cap.
    const rowsForMaxHeight = actualRows ?? minRows;
    const maxHeight = multiline && rowsForMaxHeight ? `${1.7 * rowsForMaxHeight}em` : undefined;

    return (
      <ShowOnlyTextField
        classes={classes}
        onEdit={onEdit}
        showOnlyValueComponent={currValue}
        label={label}
        disabled={disabled}
        maxHeight={maxHeight}
        width={fullWidth ? '100%' : undefined}
        doNotRenderInTypography={select || (SelectProps && !!SelectProps.renderValue)}
        staticEditIcon={staticEditIcon}
      />
    );
  }

  const clearSelection = () => {
    setFieldValue(id, isMultipleSelect ? [] : '');
    if (typeof onClear === 'function') {
      onClear();
    } else if (onClear) {
      throw new Error('onClear is defined but is not a function');
    }
  };
  return (
    <TextField
      ref={inputWrapperRef}
      disabled={disabled}
      value={inputValue}
      onChange={(value, event) => (onChange ? onChange(event) : handleChange(event))}
      onBlur={rest.select ? () => setFieldTouched(id, true) : handleBlur} // select doesn't have "id" or "name" in its onBlur
      error={!!(getIn(errors, id) && showError)}
      helperText={fieldError || helperText}
      id={id}
      multiline={multiline}
      rows={actualRows}
      minRows={minRows}
      fullWidth={fullWidth}
      // InputProps={inputPropsStyle}
      InputProps={{
        id,
        inputMode,
        type,
        ...(rest.select && clearable
          ? {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    style={{
                      padding: 4,
                      visibility:
                        staticEditIcon ||
                        ((Array.isArray(currentFieldValue) ? currentFieldValue.length > 0 : currentFieldValue) &&
                          !disabled)
                          ? 'visible'
                          : 'hidden',
                      marginRight: 15,
                    }}
                    title="Clear"
                    onClick={() => clearSelection()}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }
          : {}),
      }}
      onKeyDown={onKeyDown}
      onMouseUp={onMouseUp}
      {...rest}
    >
      {children}
    </TextField>
  );
};

TextFieldFormik.propTypes = {
  id: PropTypes.string,
  disabled: PropTypes.bool,
  showOnly: PropTypes.bool,
  onEdit: PropTypes.func,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  minRows: PropTypes.number,
  fullWidth: PropTypes.bool,
  clearable: PropTypes.bool,
  onClear: PropTypes.func,
  helperText: PropTypes.string,
  children: PropTypes.any,
  onChange: PropTypes.func,
  inputMode: PropTypes.string,
  type: PropTypes.string,
  onKeyDown: PropTypes.func,
  onMouseUp: PropTypes.func,
  inputWrapperRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(HTMLDivElement) }),
  ]),
  autoSelectSingleOption: PropTypes.bool,
};

class MultiSelectTextFieldFormikInner extends Component {
  render() {
    const {
      disabled,
      renderValue,
      options,
      classes,
      showOnly,
      onEdit,
      formik,
      renderOption,
      sortAlphabetic,
      withOptionChips,
      disabledOptions,
      addAllOption,
      allOptionValue,
      staticEditIcon,
      ...rest
    } = this.props;

    const { id } = rest;

    if (showOnly) {
      const { value, label, disabled } = rest;

      let currValue =
        value !== undefined
          ? renderValue(value)
          : formik && getIn(formik.values, id) !== undefined
            ? renderValue(getIn(formik.values, id))
            : '';

      return (
        <ShowOnlyTextField
          classes={classes}
          onEdit={onEdit}
          showOnlyValueComponent={currValue}
          label={label}
          disabled={disabled}
          staticEditIcon={staticEditIcon}
        />
      );
    }

    const { values, touched, errors, handleBlur, setFieldTouched, setFieldValue, submitCount } = formik;

    const curValue = getIn(values, id) ?? [];
    const showError = shouldShowFieldError(id, touched, submitCount);

    return (
      <div>
        <MultiSelectField
          value={curValue}
          onChange={(newValues) => setFieldValue(id, newValues)}
          onBlur={rest.select ? () => setFieldTouched(id, true) : handleBlur} // select doesn't have "id" or "name" in its onBlur
          error={getIn(errors, id) && showError}
          helperText={showError && getIn(errors, id)}
          renderValue={renderValue}
          disabled={disabled}
          name={id}
          renderOption={renderOption}
          options={options}
          sortAlphabetic={sortAlphabetic}
          withOptionChips={withOptionChips}
          disabledOptions={disabledOptions}
          addAllOption={addAllOption}
          allOptionValue={allOptionValue}
          {...rest}
        />
      </div>
    );
  }
}

MultiSelectTextFieldFormikInner.propTypes = {
  classes: PropTypes.object.isRequired,
  showOnly: PropTypes.bool,
  onEdit: PropTypes.func,
  renderValue: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  emptyChoiceValue: PropTypes.string,
  renderOption: PropTypes.func,
  formik: PropTypes.object,
  withOptionChips: PropTypes.bool,
  disabledOptions: PropTypes.array,
  addAllOption: PropTypes.bool,
  allOptionValue: PropTypes.string,
};

const MultiSelectTextFieldFormik = connect(MultiSelectTextFieldFormikInner);

const MultiSelectIdLabelTextFieldFormik = withStyles(
  connect((props) => {
    const { renderValue, options, classes, showOnly, emptyChoiceValue, onEdit, formik, allOptionId, ...rest } = props;

    const { id } = rest;
    const renderValueFromSelectedIds = (selectedIds) =>
      renderValue(options.filter((option) => selectedIds.includes(option.id)));

    if (showOnly) {
      const { value, label, disabled } = rest;

      let currValue =
        value !== undefined
          ? renderValueFromSelectedIds(value)
          : formik && getIn(formik.values, id) !== undefined
            ? renderValueFromSelectedIds(getIn(formik.values, id))
            : '';

      return (
        <ShowOnlyTextField
          classes={classes}
          onEdit={onEdit}
          showOnlyValueComponent={currValue}
          label={label}
          disabled={disabled}
          doNotRenderInTypography
        />
      );
    }

    const { values, touched, errors, handleBlur, handleChange, setFieldTouched, submitCount } = formik;

    const currValue = getIn(values, id);
    const showError = shouldShowFieldError(id, touched, submitCount);

    const isAllOptionSelected = !isNil(allOptionId) && (getIn(values, id) ?? []).includes(allOptionId);
    const isChecked = (option) => {
      const optionId = option?.id ?? option;
      if (isAllOptionSelected && optionId !== allOptionId) {
        return true;
      }
      return (getIn(values, id) ?? []).includes(optionId);
    };
    return (
      <TextField
        value={currValue || []}
        onChange={(value, e) => {
          handleChange(e);
        }}
        onBlur={rest.select ? () => setFieldTouched(id, true) : handleBlur} // select doesn't have "id" or "name" in its onBlur
        error={getIn(errors, id) && showError}
        helperText={showError && getIn(errors, id)}
        select
        SelectProps={{
          multiple: true,
          renderValue: renderValueFromSelectedIds,
        }}
        name={id}
        {...rest}
      >
        {emptyChoiceValue && (
          <MenuItem key={emptyChoiceValue} value={emptyChoiceValue}>
            <Checkbox checked={getIn(values, id).indexOf(emptyChoiceValue) > -1} />
            <ListItemText primary={emptyChoiceValue} />
          </MenuItem>
        )}
        {options.map((option) =>
          // A group header is a non-selectable divider row (e.g. "Incident categories" /
          // "Policy categories"). A disabled MenuItem rather than a ListSubheader to match the
          // relation dropdown in AutomaticRulesConfiguration/.../QueryBuilder/controls.tsx: the
          // Atomic MenuItem is already the wrapper this file uses, and a raw ListSubheader would
          // be the only one in the codebase and would owe a core/Atomic wrapper of its own. MUI
          // excludes a disabled row from both keyboard navigation and the initial-highlight
          // lookahead (MenuList moveFocus and its activeItemIndex check), which is the behaviour
          // the header needs.
          option.isGroupHeader ? (
            <MenuItem key={option.id} disabled className="!min-h-0 !py-4 !opacity-100">
              <ListItemText
                primary={option.label}
                primaryTypographyProps={{ className: '!text-xs !font-semibold uppercase !text-slate-700' }}
              />
            </MenuItem>
          ) : (
            <MenuItem key={option.id} value={option.id}>
              <Checkbox checked={isChecked(option)} />
              <ListItemText primary={option.label} />
            </MenuItem>
          )
        )}
      </TextField>
    );
  }),
  styles
);

MultiSelectIdLabelTextFieldFormik.propTypes = {
  renderValue: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.any, label: PropTypes.any })).isRequired,
  allOptionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

class DatePickerTextFieldFormikInner extends Component {
  state = {
    datePickerError: '',
  };

  render() {
    const {
      classes,
      showOnly,
      onEdit,
      formik,
      placeholder,
      format,
      containerClassName,
      nullIfEmpty,
      staticEditIcon,
      helperText,
      ...rest
    } = this.props;
    const { datePickerError } = this.state;

    const { id } = rest;

    if (showOnly) {
      const { label, value, disabled } = this.props;

      // if someone supplied a value, use it, otherwise, lookup the values array
      let currValue =
        value !== undefined ? value : formik && getIn(formik.values, id) !== undefined ? getIn(formik.values, id) : '';
      if (currValue) {
        currValue = isoDateToUs(currValue);
      }

      return (
        <ShowOnlyTextField
          classes={classes}
          onEdit={onEdit}
          showOnlyValueComponent={currValue}
          label={label}
          disabled={disabled}
          staticEditIcon={staticEditIcon}
        />
      );
    }

    const { values, touched, errors, setFieldTouched, setFieldValue, submitCount } = formik;

    const getEmptyValue = () => (nullIfEmpty ? null : '');
    const showError = shouldShowFieldError(id, touched, submitCount);

    return (
      <div className={containerClassName}>
        <DatePickerField
          format={format}
          value={getIn(values, id)} // new Date() assumes UTC, moment assumes local time, values contains local time
          placeholder={placeholder}
          onChange={(date) => setFieldValue(id, date ? date.format('YYYY-MM-DD') : getEmptyValue())} // date is a moment object
          // onClose={() => setFieldTouched(id, true)} // calendar was opened and closed -- don't use! currently will trigger Formik verification but if we just chose a date, it might not have updated and verification will be on older value (and will remain)
          onBlur={() => setFieldTouched(id, true)}
          error={showError && !!(datePickerError || getIn(errors, id))}
          onError={(error) => datePickerError !== error && this.setState({ datePickerError: error })}
          helperText={(showError && (datePickerError || getIn(errors, id))) || helperText}
          {...rest}
        />
      </div>
    );
  }
}

const DatePickerTextFieldFormik = connect(DatePickerTextFieldFormikInner);

class TimePickerTextFieldFormikInner extends Component {
  state = {
    timePickerError: '',
  };

  render() {
    const { classes, showOnly, onEdit, formik, setTouchedOnChange = false, nullIfEmpty, ...rest } = this.props;

    const { id } = rest;

    const { timePickerError } = this.state;
    if (showOnly) {
      const { label, value, disabled } = this.props;

      // if someone supplied a value, use it, otherwise, lookup the values array
      let currValue =
        value !== undefined ? value : formik && getIn(formik.values, id) !== undefined ? getIn(formik.values, id) : '';

      // Presenting the date with the right format, if not empty
      if (currValue) currValue = localTimeToViewTime(currValue);

      return (
        <ShowOnlyTextField
          classes={classes}
          onEdit={onEdit}
          showOnlyValueComponent={currValue}
          label={label}
          disabled={disabled}
        />
      );
    }

    const { values, touched, errors, setFieldTouched, setFieldValue, submitCount } = formik;

    const showError = shouldShowFieldError(id, touched, submitCount);
    return (
      <div>
        <DatePickerField
          isOnlyTimeSelection
          value={getIn(values, id)}
          onChange={(date) => {
            if (setTouchedOnChange) {
              setFieldTouched(id, true);
            }
            setFieldValue(id, date ? date.format('HH:mm') : nullIfEmpty ? null : '');
          }} // date is a moment object
          // onClose={() => setFieldTouched(id, true)} // clock was opened and closed -- don't use! currently will trigger Formik verification but if we just chose a date, it might not have updated and verification will be on older value (and will remain)
          onBlur={() => setFieldTouched(id, true)}
          onError={(error) => timePickerError !== error && this.setState({ timePickerError: error })}
          helperText={showError && (timePickerError || getIn(errors, id))}
          error={showError && !!(timePickerError || getIn(errors, id))}
          {...rest}
        />
      </div>
    );
  }
}

const TimePickerTextFieldFormik = connect(TimePickerTextFieldFormikInner);

function YearPickerSelectTextFieldFormikInner(props) {
  const { formik, id, label, showOnly, ...rest } = props;
  const { values, setFieldValue, setFieldTouched } = formik;

  return (
    <PatternFormat
      customInput={TextFieldFormik}
      id={id}
      label={label}
      format="####"
      mask="_"
      value={getIn(values, id)}
      onValueChange={onUserValueChange((numberValues) => {
        const { floatValue } = numberValues;
        setFieldValue(id, floatValue === undefined ? '' : floatValue);
      })}
      onBlur={() => setFieldTouched(id, true)}
      showOnly={showOnly}
      {...rest}
    />
  );
}

YearPickerSelectTextFieldFormikInner.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  showOnly: PropTypes.bool,
  formik: PropTypes.object.isRequired,
};

const YearPickerSelectTextFieldFormik = connect(YearPickerSelectTextFieldFormikInner);

function NumericTextFieldFormik(props) {
  const { id, label, onChange, ...rest } = props;
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const { groupSeparator, decimalSeparator } = useCurrencyFormatter();

  return (
    <NumericFormat
      customInput={TextFieldFormik}
      thousandSeparator={groupSeparator ? groupSeparator : true}
      decimalSeparator={decimalSeparator || '*'} // decimal separator must have 1 character value
      decimalScale={!decimalSeparator ? 0 : 2} // if currency does not have decimalSeparator set decimalScale to 0
      id={id}
      label={label}
      value={getIn(values, id)}
      onValueChange={onUserValueChange((numberValues) => {
        const { floatValue } = numberValues;
        setFieldValue(id, floatValue === undefined ? null : floatValue);
        if (onChange) onChange(floatValue);
      })}
      onBlur={() => setFieldTouched(id, true)}
      {...rest}
    />
  );
}

NumericTextFieldFormik.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  className: PropTypes.string,
  thousandSeparator: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isAllowed: PropTypes.func,
  decimalSeparator: PropTypes.string,
  decimalScale: PropTypes.number,
  fixedDecimalScale: PropTypes.bool,
  allowNegative: PropTypes.bool,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool, // passed on the the custom input (TextFieldFormik)
  fullWidth: PropTypes.bool, // passed on the the custom input (TextFieldFormik)
};

const DatePickerTextFieldFormikWithStyles = withStyles(DatePickerTextFieldFormik, styles);
const TimePickerTextFieldFormikWithStyles = withStyles(TimePickerTextFieldFormik, styles);
const MultiSelectTextFieldFormikWithStyles = withStyles(MultiSelectTextFieldFormik, styles);

export {
  DatePickerTextFieldFormikWithStyles as DatePickerTextFieldFormik,
  MonetaryValueTextFieldFormik,
  MultiSelectIdLabelTextFieldFormik,
  MultiSelectTextFieldFormikWithStyles as MultiSelectTextFieldFormik,
  NumericTextFieldFormik,
  ShowOnlyTextField,
  TextFieldFormik,
  TimePickerTextFieldFormikWithStyles as TimePickerTextFieldFormik,
  YearPickerSelectTextFieldFormik,
};

export default TextFieldFormik;

export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value; //assign the value of ref to the argument
  }, [value]); //this code will run when the value of 'value' changes
  return ref.current; //in the end, return the current ref value.
}

export function useAllOptionOnMultiSelect(listValue, listId, allOption) {
  const { setFieldValue } = useFormikContext();
  const prevRef = usePrevious(listValue);

  useEffect(() => {
    if (!isNil(allOption) && listValue.length > 1 && listValue.includes(allOption)) {
      if (!prevRef.includes(allOption)) {
        setFieldValue(listId, [allOption]);
      } else {
        setFieldValue(
          listId,
          listValue.filter((option) => option !== allOption)
        );
      }
    }
  }, [listValue, listId, setFieldValue, prevRef, allOption]);
}

export function useSetDefaultFieldsOnChange(
  value,
  fieldsWithDefaultValues,
  onEveryChange,
  disabled,
  doNotIgnoreEmptyPrev,
  ignoreOnMount = false
) {
  const { setFieldValue } = useFormikContext();
  const prevRef = usePrevious(value);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    // When ignoreOnMount, skip the first effect run so a pre-populated `value`
    // doesn't clobber initial field values. After mount, an empty prev becomes
    // a real "first selection" signal and should fire (hence `|| ignoreOnMount`
    // in the guard below).
    if (ignoreOnMount && !hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    hasMountedRef.current = true;
    if (prevRef !== value) {
      // to prevent clearing the initial values
      if ((prevRef || doNotIgnoreEmptyPrev || ignoreOnMount) && !disabled) {
        Object.keys(fieldsWithDefaultValues).forEach((id) => {
          setFieldValue(id, fieldsWithDefaultValues[id]);
        });
      }
      !disabled && onEveryChange && onEveryChange();
    }
  }, [
    value,
    setFieldValue,
    prevRef,
    fieldsWithDefaultValues,
    onEveryChange,
    disabled,
    doNotIgnoreEmptyPrev,
    ignoreOnMount,
  ]);
}
